import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import useStore from "@/services/store";
import { capture, initSentry } from "@/services/sentry";
import "@/locales"; // Initialize i18n

// RECRUITER
import Auth from "./pages/auth";
import Connect from "./pages/auth/connect";
import OauthAuthorize from "./pages/auth/oauth-authorize";

import Admin from "./pages/admin";
import Recruiter from "./pages/recruiter";
import Agency from "./pages/agency";
import Candidate from "./pages/candidate";
import Chat from "./pages/chat";
import PublicResume from "./pages/public-resume";
import PublicShortlist from "./pages/public-shortlist";
import AuthRecruiter from "./pages/auth/recruiter";
import PrivacyPolicy from "./pages/privacy-policy";
import Mcp from "./pages/mcp";

import Loader from "./components/loader";

import API from "./services/api";

initSentry();

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" />
      <Routes>
        <Route path="/privacy-policy/*" element={<PrivacyPolicy />} />
        <Route element={<AuthLayout />}>
          <Route path="/auth/connect" element={<Connect />} />
          <Route path="/auth/*" element={<Auth />} />
        </Route>

        <Route path="/admin/*" element={<Admin />} />
        <Route path="/agency/*" element={<Agency />} />
        <Route path="/candidate/*" element={<Candidate />} />
        <Route path="/recruiter/*" element={<Recruiter />} />
        <Route path="/chat/*" element={<Chat />} />

        {/* Public Routes */}
        <Route path="/mcp" element={<Mcp />} />
        <Route path="/auth-recruiter" element={<AuthRecruiter />} />
        <Route path="/resume/:token" element={<PublicResume />} />
        <Route path="/share/stage/:id" element={<PublicShortlist />} />
        <Route path="/oauth/authorize" element={<OauthAuthorize />} />

        <Route path="/" element={<FallbackRoute />} />
      </Routes>
    </BrowserRouter>
  );
}

const defaultRouteForUser = (user) => {
  if (user.role_recruiter) return "/recruiter";
  if (user.role_admin) return "/admin";
  if (user.role_candidate) return "/candidate";
  if (user.role_agency) return "/agency";
  return "/";
};

const AuthLayout = () => {
  const { user } = useStore();
  if (!user) return <Outlet />;
  return <Navigate to={defaultRouteForUser(user)} replace={true} />;
};

const FallbackRoute = () => {
  const { user, setUser, setOrganization } = useStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyToken();
  }, []);

  async function verifyToken() {
    try {
      setLoading(true);
      const { ok, token, user, organisations } = await API.get("/user/signin_token");
      if (!ok) {
        setUser(null);
        setOrganization(null);
        return;
      }
      if (token) API.setToken(token);
      setUser(user);
      const stored = useStore.getState().organization;
      const stillValid = stored && organisations?.find((o) => o._id === (stored._id || stored.id));
      setOrganization(stillValid ? stored : organisations?.[0] || null);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace={true} />;

  return <Navigate to={defaultRouteForUser(user)} replace={true} />;
};
